import { Node, mergeAttributes } from '@tiptap/core';
import { NodeViewProps, NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';

import React from 'react';

import { VariablePill } from './variable-pill';
import { variablePillClasses } from './variable-pill-classes';

export interface VariableNodeAttrs {
	id: string;
	token: string;
	label: string;
	prefix?: string;
}

// React component to render the variable
const VariableComponent: React.FC<NodeViewProps> = ({ node }) => {
	return (
		<NodeViewWrapper
			as="span"
			contentEditable={false}
			draggable={false}
		>
			<VariablePill {...(node.attrs as VariableNodeAttrs)} />
		</NodeViewWrapper>
	);
};

// Define the Tiptap Variable node
export const VariableNode = Node.create<Record<string, never>>({
	name: 'variable',

	group: 'inline',

	inline: true,

	selectable: true,

	atom: true,

	addAttributes() {
		return {
			id: {
				default: null,
				parseHTML: (element) => element.getAttribute('data-id'),
				renderHTML: (attributes) => {
					if (!attributes.id) {
						return {};
					}
					return {
						'data-id': attributes.id,
					};
				},
			},
			token: {
				default: null,
				parseHTML: (element) => element.getAttribute('data-token'),
				renderHTML: (attributes) => {
					if (!attributes.token) {
						return {};
					}
					return {
						'data-token': attributes.token,
					};
				},
			},
			label: {
				default: null,
				parseHTML: (element) => element.getAttribute('data-label'),
				renderHTML: (attributes) => {
					if (!attributes.label) {
						return {};
					}
					return {
						'data-label': attributes.label,
					};
				},
			},
			prefix: {
				default: null,
				parseHTML: (element) => element.getAttribute('data-prefix'),
				renderHTML: (attributes) => {
					if (!attributes.prefix) return {};
					return { 'data-prefix': attributes.prefix };
				},
			},
		};
	},

	parseHTML() {
		return [
			{
				tag: 'span[data-type="variable"]',
			},
		];
	},

	renderHTML({ HTMLAttributes }) {
		const prefix = HTMLAttributes['data-prefix'];
		const label = HTMLAttributes['data-label'] || '';
		return [
			'span',
			mergeAttributes(HTMLAttributes, {
				'data-type': 'variable',
				class: variablePillClasses,
			}),
			prefix ? `${prefix}${label}` : `{${label}}`,
		];
	},

	addNodeView() {
		return ReactNodeViewRenderer(VariableComponent);
	},
});

export default VariableNode;
